/* ==========================================================================
   Atelier Calzamoda — motor de animaciones e interfaz
   GSAP 3 + ScrollTrigger + SplitText + CustomEase + Lenis
   ========================================================================== */
(function () {
  'use strict';

  var S = window.themeSettings || {};
  var root = document.documentElement;
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HAS_GSAP = !!(window.gsap && window.ScrollTrigger);
  var MOTION = HAS_GSAP && !REDUCE;

  root.classList.add(MOTION ? 'motion' : 'no-motion');

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  /* ---------- GSAP: plugins y curvas propias ---------- */
  if (HAS_GSAP) {
    var plugins = [window.ScrollTrigger];
    if (window.SplitText) plugins.push(window.SplitText);
    if (window.CustomEase) plugins.push(window.CustomEase);
    gsap.registerPlugin.apply(gsap, plugins);
    if (window.CustomEase) {
      CustomEase.create('silk', 'M0,0 C0.19,1 0.22,1 1,1');
      CustomEase.create('curtain', 'M0,0 C0.76,0 0.24,1 1,1');
    }
    gsap.defaults({ ease: window.CustomEase ? 'silk' : 'expo.out', duration: 1.1 });
    ScrollTrigger.config({ ignoreMobileResize: true });
  }

  /* ---------- Scroll suave (Lenis) ---------- */
  var lenis = null;
  if (MOTION && S.smoothScroll && !S.designMode && window.Lenis) {
    lenis = new Lenis({ lerp: 0.085, smoothWheel: true, anchors: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
  }
  var stopScroll = function () { lenis ? lenis.stop() : document.body.classList.add('is-locked'); };
  var startScroll = function () { lenis ? lenis.start() : document.body.classList.remove('is-locked'); };

  var canSplit = function () { return MOTION && !!window.SplitText; };
  var fontsReady = function () {
    var timeout = new Promise(function (r) { setTimeout(r, 1500); });
    return Promise.race([document.fonts ? document.fonts.ready : Promise.resolve(), timeout]);
  };

  /* ==========================================================================
     Módulos de animación (uno por sección con data-module)
     ========================================================================== */

  /* Hero: la imagen crece desde una tarjeta hasta pantalla completa */
  function heroExpand(el) {
    var media = $('[data-hero-media]', el);
    var img = $('.hero__img, .placeholder', media);
    var left = $('[data-hero-left]', el);
    var right = $('[data-hero-right]', el);
    var overlay = $('[data-hero-overlay]', el);
    var hint = $('.hero__hint', el);
    var isPack = media.classList.contains('is-packshot');
    var mm = gsap.matchMedia();

    mm.add({ mobile: '(max-width: 749px)', desktop: '(min-width: 750px)' }, function (ctx) {
      var mobile = ctx.conditions.mobile;
      var start = mobile ? 'inset(23% 12% 23% 12%)' : 'inset(24% 34% 24% 34%)';
      gsap.set(overlay, { autoAlpha: 0, y: 50 });

      var tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: { trigger: el, start: 'top top', end: '+=175%', scrub: 1.1, pin: true, anticipatePin: 1 }
      });
      tl.fromTo(media, { clipPath: start }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1 }, 0)
        .fromTo(img, { scale: isPack ? 1.12 : 1.45 }, { scale: 1, duration: 1 }, 0)
        .fromTo(left, { xPercent: 0, yPercent: 0, autoAlpha: 1 },
          { xPercent: mobile ? 0 : -55, yPercent: mobile ? -80 : 0, autoAlpha: 0, duration: 0.55, immediateRender: false }, 0)
        .fromTo(right, { xPercent: 0, yPercent: 0, autoAlpha: 1 },
          { xPercent: mobile ? 0 : 55, yPercent: mobile ? 80 : 0, autoAlpha: 0, duration: 0.55, immediateRender: false }, 0);
      if (hint) tl.to(hint, { autoAlpha: 0, duration: 0.12 }, 0);
      tl.to(overlay, { autoAlpha: 1, y: 0, duration: 0.3, ease: 'power2.out' }, 0.72)
        .to({}, { duration: 0.2 });
    });

    return function () { mm.revert(); };
  }

  /* Animación de entrada del hero (después de la cortina) */
  function heroIntro() {
    var el = $('[data-module="hero-expand"]');
    if (!el || !MOTION) return;
    var words = $$('.hero__word', el);
    var media = $('[data-hero-media]', el);
    var tl = gsap.timeline({ delay: 0.05 });
    gsap.set(words, { opacity: 1 });
    tl.from(media, { autoAlpha: 0, y: 70, duration: 1.6 }, 0);
    if (canSplit()) {
      words.forEach(function (w, i) {
        var split = SplitText.create(w, { type: 'words,chars', mask: 'words' });
        tl.from(split.chars, { yPercent: 115, duration: 1.4, stagger: 0.04 }, 0.15 + i * 0.12);
      });
    } else {
      tl.from(words, { autoAlpha: 0, y: 40, stagger: 0.12 }, 0.15);
    }
    var hint = $('.hero__hint', el);
    if (hint) tl.from(hint.children, { autoAlpha: 0, y: 16, duration: 1, stagger: 0.1 }, 0.8);
  }

  /* Cinta infinita que acelera con la velocidad del scroll */
  function marquee(el) {
    var track = $('[data-marquee-track]', el);
    var loop = gsap.to(track, { xPercent: -50, ease: 'none', duration: 38, repeat: -1 });
    var speed = { s: 1 };
    var boost = gsap.quickTo(speed, 's', {
      duration: 0.6, ease: 'power3.out',
      onUpdate: function () { loop.timeScale(speed.s); }
    });
    var settle = null;
    ScrollTrigger.create({
      trigger: el, start: 'top bottom', end: 'bottom top',
      onUpdate: function (self) {
        var v = Math.min(Math.abs(self.getVelocity()) / 220, 6);
        boost(1 + v);
        if (settle) settle.kill();
        settle = gsap.delayedCall(0.18, function () { boost(1); });
      },
      onToggle: function (self) { self.isActive ? loop.play() : loop.pause(); }
    });
  }

  /* Frase: las palabras se iluminan una a una */
  function statement(el) {
    var text = $('[data-statement-text]', el);
    if (!text || !canSplit()) return;
    var split = SplitText.create(text, { type: 'words' });
    gsap.fromTo(split.words, { opacity: 0.12 }, {
      opacity: 1, ease: 'none', stagger: 0.1,
      scrollTrigger: { trigger: text, start: 'top 82%', end: 'bottom 40%', scrub: true }
    });
    return function () { split.revert(); };
  }

  /* Galería por capas: cada imagen se expande y viaja a distinta velocidad */
  function parallaxGallery(el) {
    var items = $$('.pgal__item', el);
    var mm = gsap.matchMedia();
    mm.add({ mobile: '(max-width: 749px)', desktop: '(min-width: 750px)' }, function (ctx) {
      var depth = ctx.conditions.mobile ? 0.22 : 0.75;
      items.forEach(function (item) {
        var speed = parseFloat(item.dataset.speed) || 1;
        var frame = $('[data-pgal-frame]', item);
        var img = $('img, svg', frame);
        var reveal = { trigger: item, start: 'top bottom', end: 'center 60%', scrub: 1 };
        gsap.fromTo(frame, { clipPath: 'inset(24% 20% 24% 20%)' },
          { clipPath: 'inset(0% 0% 0% 0%)', ease: 'none', scrollTrigger: reveal });
        if (img) gsap.fromTo(img, { scale: 1.4 }, { scale: 1, ease: 'none', scrollTrigger: reveal });
        gsap.fromTo(item, { y: function () { return (speed - 1) * window.innerHeight * depth; } }, {
          y: function () { return (1 - speed) * window.innerHeight * depth; },
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true }
        });
      });
    });
    return function () { mm.revert(); };
  }

  /* Rejillas de productos: aparición escalonada con cortina */
  function revealGrid(el) {
    var items = $$('[data-reveal-item]', el);
    if (!items.length) return;
    gsap.set(items, { autoAlpha: 0, y: 60 });
    ScrollTrigger.batch(items, {
      start: 'top 92%',
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, { autoAlpha: 1, y: 0, duration: 1.2, stagger: 0.09, overwrite: true });
        var medias = batch.map(function (b) { return $('.card__media', b); }).filter(Boolean);
        gsap.fromTo(medias, { clipPath: 'inset(22% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.5, stagger: 0.09 });
      }
    });
  }

  /* Colecciones: scroll horizontal fijado con imágenes que se abren */
  function hScroll(el) {
    var track = $('[data-hs-track]', el);
    var progress = $('[data-hs-progress]', el);
    var mm = gsap.matchMedia();
    mm.add('(min-width: 750px)', function () {
      var distance = function () { return Math.max(0, track.scrollWidth - window.innerWidth); };
      var tween = gsap.to(track, {
        x: function () { return -distance(); },
        ease: 'none',
        scrollTrigger: {
          trigger: el, start: 'top top', end: function () { return '+=' + distance(); },
          pin: true, scrub: 1, anticipatePin: 1, invalidateOnRefresh: true,
          onUpdate: function (self) { if (progress) gsap.set(progress, { scaleX: self.progress }); }
        }
      });
      $$('.hs__panel', track).forEach(function (panel) {
        var media = $('[data-hs-media]', panel);
        var img = $('img, svg', media);
        gsap.fromTo(media, { clipPath: 'inset(14% 12% 14% 12%)' }, {
          clipPath: 'inset(0% 0% 0% 0%)', ease: 'none',
          scrollTrigger: { trigger: panel, containerAnimation: tween, start: 'left 100%', end: 'left 40%', scrub: true }
        });
        if (img && !media.classList.contains('is-packshot')) {
          gsap.fromTo(img, { xPercent: -8 }, {
            xPercent: 8, ease: 'none',
            scrollTrigger: { trigger: panel, containerAnimation: tween, start: 'left right', end: 'right left', scrub: true }
          });
        } else if (img) {
          gsap.fromTo(img, { scale: 0.82, rotate: -4 }, {
            scale: 1, rotate: 0, ease: 'none',
            scrollTrigger: { trigger: panel, containerAnimation: tween, start: 'left 100%', end: 'left 40%', scrub: true }
          });
        }
      });
    });
    return function () { mm.revert(); };
  }

  /* Llamado final: la tarjeta se abre a ancho completo */
  function ctaExpand(el) {
    var media = $('[data-cta-media]', el);
    var img = $('.cta__img, .placeholder', media);
    var mm = gsap.matchMedia();
    mm.add({ mobile: '(max-width: 749px)', desktop: '(min-width: 750px)' }, function (ctx) {
      var start = ctx.conditions.mobile ? 'inset(6% 6% 6% 6%)' : 'inset(10% 24% 10% 24%)';
      gsap.fromTo(media, { clipPath: start }, {
        clipPath: 'inset(0% 0% 0% 0%)', ease: 'none',
        scrollTrigger: { trigger: el, start: 'top 90%', end: 'top 5%', scrub: 1 }
      });
      if (img) {
        gsap.fromTo(img, { yPercent: -6, scale: 1.25 }, {
          yPercent: 6, scale: 1, ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
        });
      }
    });
    return function () { mm.revert(); };
  }

  /* Departamentos: cada tarjeta se abre y la foto corre en parallax */
  function departments(el) {
    var cards = $$('[data-dept-card]', el);
    var mm = gsap.matchMedia();
    mm.add({ mobile: '(max-width: 989px)', desktop: '(min-width: 990px)' }, function (ctx) {
      cards.forEach(function (card, i) {
        var media = $('[data-dept-media]', card);
        var img = $('img, svg', media);
        var rise = ctx.conditions.mobile ? 0 : (i % 2 ? 60 : 0);
        gsap.fromTo(media, { clipPath: 'inset(22% 18% 22% 18%)' }, {
          clipPath: 'inset(0% 0% 0% 0%)', ease: 'none',
          scrollTrigger: { trigger: card, start: 'top 96%', end: 'top 40%', scrub: 1 }
        });
        if (rise) {
          gsap.fromTo(card, { y: rise }, {
            y: -rise, ease: 'none',
            scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
          });
        }
        if (img) {
          gsap.fromTo(img, { yPercent: -5, scale: 1.18 }, {
            yPercent: 5, scale: 1, ease: 'none',
            scrollTrigger: { trigger: card, start: 'top bottom', end: 'bottom top', scrub: true }
          });
        }
      });
    });
    return function () { mm.revert(); };
  }

  /* Pie de página: la palabra gigante sube letra por letra */
  function footerMark(el) {
    var mark = $('[data-footer-mark]', el);
    if (!mark || !canSplit()) return;
    var split = SplitText.create(mark, { type: 'chars' });
    gsap.fromTo(split.chars, { yPercent: 105 }, {
      yPercent: 0, ease: 'none', stagger: 0.06,
      scrollTrigger: { trigger: mark, start: 'top bottom', end: 'bottom bottom', scrub: 1 }
    });
    return function () { split.revert(); };
  }

  /* Revelados genéricos: títulos por líneas, textos, imágenes */
  var seen = new WeakSet();
  function genericReveals(scope) {
    $$('[data-split-lines]', scope).forEach(function (h) {
      if (seen.has(h)) return; seen.add(h);
      if (!canSplit()) return;
      SplitText.create(h, {
        type: 'lines', mask: 'lines', linesClass: 'split-line', autoSplit: true,
        onSplit: function (self) {
          return gsap.from(self.lines, {
            yPercent: 110, duration: 1.3, stagger: 0.1,
            scrollTrigger: { trigger: h, start: 'top 90%', once: true }
          });
        }
      });
    });
    $$('[data-reveal]', scope).forEach(function (n) {
      if (seen.has(n)) return; seen.add(n);
      gsap.from(n, { autoAlpha: 0, y: 28, duration: 1.2, scrollTrigger: { trigger: n, start: 'top 92%', once: true } });
    });
    $$('[data-img-reveal]', scope).forEach(function (fig) {
      if (seen.has(fig)) return; seen.add(fig);
      var img = $('img, svg', fig);
      var st = { trigger: fig, start: 'top 92%', once: true };
      gsap.fromTo(fig, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.5, ease: window.CustomEase ? 'curtain' : 'expo.inOut', scrollTrigger: st });
      if (img) gsap.fromTo(img, { scale: 1.3 }, { scale: 1, duration: 1.8, scrollTrigger: st });
    });
  }

  var MODULES = {
    'hero-expand': heroExpand,
    'marquee': marquee,
    'statement': statement,
    'parallax-gallery': parallaxGallery,
    'departments': departments,
    'reveal-grid': revealGrid,
    'h-scroll': hScroll,
    'cta-expand': ctaExpand,
    'footer': footerMark
  };
  var live = new Map();

  function mount(scope) {
    if (!MOTION) return;
    var nodes = $$('[data-module]', scope);
    if (scope.matches && scope.matches('[data-module]')) nodes.unshift(scope);
    nodes.forEach(function (el) {
      if (live.has(el)) return;
      var fn = MODULES[el.dataset.module];
      var cleanup = null;
      var ctx = gsap.context(function () {
        if (fn) cleanup = fn(el);
        genericReveals(el);
      }, el);
      live.set(el, { ctx: ctx, cleanup: cleanup });
    });
    // Elementos fuera de módulos (producto, páginas, artículos)
    gsap.context(function () { genericReveals(scope); });
  }

  function unmount(scope) {
    var nodes = $$('[data-module]', scope);
    if (scope.matches && scope.matches('[data-module]')) nodes.unshift(scope);
    nodes.forEach(function (el) {
      var rec = live.get(el);
      if (!rec) return;
      if (typeof rec.cleanup === 'function') rec.cleanup();
      rec.ctx.revert();
      live.delete(el);
    });
  }

  /* ---------- Señal de "página lista" (después de la cortina) ---------- */
  var isReady = false;
  function signalReady() {
    if (isReady) return;
    isReady = true;
    window.dispatchEvent(new CustomEvent('atelier:ready'));
  }
  function whenReady(fn) {
    if (isReady) fn(); else window.addEventListener('atelier:ready', fn, { once: true });
  }

  /* ---------- Cortina de entrada ---------- */
  function runIntro() {
    var intro = $('[data-intro]');
    var done = function () {
      if (intro && intro.parentNode) intro.parentNode.removeChild(intro);
      startScroll();
      heroIntro();
      signalReady();
    };
    if (!intro) { heroIntro(); signalReady(); return; }
    if (!MOTION || root.classList.contains('intro-seen')) { done(); return; }
    try { sessionStorage.setItem('cm-intro', '1'); } catch (e) { /* sin almacenamiento */ }
    stopScroll();
    window.scrollTo(0, 0);
    intro.style.animation = 'none';
    var word = $('[data-intro-word]', intro);
    var line = $('[data-intro-line]', intro);
    var tl = gsap.timeline({ onComplete: done });
    tl.from(word, { autoAlpha: 0, scale: 0.86, y: 24, duration: 1.2 }, 0);
    tl.from(line, { scaleX: 0, duration: 1.1 }, 0.25)
      .to(intro, { clipPath: 'inset(0% 0% 100% 0%)', duration: 1.15, ease: window.CustomEase ? 'curtain' : 'expo.inOut' }, '+=0.15');
  }

  /* ==========================================================================
     Interfaz (funciona aunque no haya animaciones)
     ========================================================================== */

  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;
    var lastY = window.scrollY;
    var ticking = false;
    var update = function () {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      if (y > lastY + 4 && y > 320 && !document.body.classList.contains('menu-open')) header.classList.add('is-hidden');
      else if (y < lastY - 4 || y < 320) header.classList.remove('is-hidden');
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  function initMenu() {
    var drawer = $('[data-menu-drawer]');
    var openBtn = $('[data-menu-open]');
    if (!drawer || !openBtn) return;
    var close = function () {
      drawer.classList.remove('is-open');
      document.body.classList.remove('menu-open');
      openBtn.setAttribute('aria-expanded', 'false');
      startScroll();
      setTimeout(function () { drawer.hidden = true; openBtn.focus(); }, 500);
    };
    var open = function () {
      drawer.hidden = false;
      document.body.classList.add('menu-open');
      openBtn.setAttribute('aria-expanded', 'true');
      stopScroll();
      requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      var closeBtn = $('.menu-drawer__close', drawer);
      if (closeBtn) closeBtn.focus();
      if (MOTION) gsap.fromTo($$('.menu-drawer__link', drawer), { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, stagger: 0.05, duration: 0.9, delay: 0.2 });
    };
    openBtn.addEventListener('click', open);
    $$('[data-menu-close]', drawer).forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !drawer.hidden) close(); });
  }

  /* Formato de dinero de Shopify */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = (format || '${{amount}}').replace(/<[^>]*>/g, '');
    var re = /\{\{\s*(\w+)\s*\}\}/;
    var delim = function (n, precision, thousands, decimal) {
      thousands = thousands || ','; decimal = decimal || '.';
      if (isNaN(n) || n == null) return '0';
      var parts = (n / 100).toFixed(precision).split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');
    };
    var m = format.match(re);
    if (!m) return format;
    var v;
    switch (m[1]) {
      case 'amount_no_decimals': v = delim(cents, 0); break;
      case 'amount_with_comma_separator': v = delim(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = delim(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = delim(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': v = delim(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': v = delim(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': v = delim(cents, 2, ' ', '.'); break;
      default: v = delim(cents, 2);
    }
    return format.replace(re, v);
  }

  var toastTimer;
  function toast(message, href, label) {
    var t = $('[data-toast]');
    if (!t) return;
    t.innerHTML = '';
    var span = document.createElement('span');
    span.textContent = message;
    t.appendChild(span);
    if (href) {
      var a = document.createElement('a');
      a.href = href; a.textContent = label || 'Ver';
      t.appendChild(a);
    }
    t.hidden = false;
    requestAnimationFrame(function () { t.classList.add('is-visible'); });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      t.classList.remove('is-visible');
      setTimeout(function () { t.hidden = true; }, 600);
    }, 4200);
  }

  function setCartCount(count) {
    $$('[data-cart-count]').forEach(function (n) { n.textContent = count; n.hidden = count === 0; });
    $$('[data-cart-link]').forEach(function (n) { n.setAttribute('aria-label', 'Carrito (' + count + ')'); });
  }

  function initProduct(scope) {
    $$('[data-product-section]', scope).forEach(function (sec) {
      if (sec.dataset.ready) return;
      sec.dataset.ready = '1';
      var jsonEl = $('[data-product-json]', sec);
      if (!jsonEl) return;
      var product = JSON.parse(jsonEl.textContent);
      var form = $('[data-product-form]', sec) || $('form[action*="/cart/add"]', sec);
      var idInput = $('[data-variant-id]', sec);
      var fieldsets = $$('[data-option-index]', sec);
      var btn = $('[data-add-button]', sec);
      var label = $('[data-add-label]', sec);
      var priceEl = $('[data-price-current]', sec);
      var compareEl = $('[data-price-compare]', sec);
      var wa = $('[data-wa-product]', sec);
      var gallery = $('[data-pdp-gallery]', sec);
      var errEl = $('[data-form-error]', sec);

      var selected = function () {
        return fieldsets.map(function (fs) { var c = $('input:checked', fs); return c ? c.value : null; });
      };
      var matches = function (v, sel) { return v.options.every(function (o, i) { return o === sel[i]; }); };

      // Disponibilidad en cascada: cada opción sólo depende de las anteriores
      // (un color no se tacha porque la talla elegida esté agotada en él).
      var refreshAvailability = function (sel) {
        fieldsets.forEach(function (fs, i) {
          $$('input', fs).forEach(function (input) {
            var ok = product.variants.some(function (v) {
              if (!v.available || v.options[i] !== input.value) return false;
              for (var j = 0; j < i; j++) { if (v.options[j] !== sel[j]) return false; }
              return true;
            });
            input.nextElementSibling.classList.toggle('is-unavailable', !ok);
          });
        });
      };

      var onChange = function () {
        var sel = selected();
        fieldsets.forEach(function (fs, i) { var out = $('[data-option-value]', fs); if (out) out.textContent = sel[i]; });
        refreshAvailability(sel);
        var v = product.variants.find(function (x) { return matches(x, sel); });
        if (!v) { btn.disabled = true; label.textContent = 'No disponible'; return; }
        idInput.value = v.id;
        priceEl.textContent = formatMoney(v.price, S.moneyFormat);
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.textContent = formatMoney(v.compare_at_price, S.moneyFormat);
          compareEl.hidden = false;
        } else { compareEl.hidden = true; }
        btn.disabled = !v.available;
        label.textContent = v.available ? 'Agregar al carrito' : 'Agotado';
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* noop */ }
        if (wa) {
          var text = wa.dataset.waGreeting + ' ' + wa.dataset.productTitle + ' (' + v.title + ') — ' + wa.dataset.productUrl + '?variant=' + v.id;
          wa.href = 'https://wa.me/' + wa.dataset.waBase + '?text=' + encodeURIComponent(text);
        }
                if (v.featured_image && gallery) {
          var fig = $('[data-media-id="' + v.featured_image.id + '"]', gallery);
          if (fig) fig.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
      };

      fieldsets.forEach(function (fs) { fs.addEventListener('change', onChange); });
      if (fieldsets.length) refreshAvailability(selected());

      $$('[data-qty]', sec).forEach(function (b) {
        b.addEventListener('click', function () {
          var input = $('.qty__input', b.parentNode);
          input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(b.dataset.qty, 10));
        });
      });

      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          if (btn.disabled) return;
          btn.classList.add('is-loading');
          if (errEl) errEl.hidden = true;
          var body = new FormData();
          body.append('id', idInput.value);
          body.append('quantity', ($('.qty__input', form) || { value: 1 }).value);
          fetch((S.cartAddUrl || '/cart/add') + '.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: body
          })
            .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
            .then(function (res) {
              if (!res.ok || res.data.status) throw new Error(res.data.description || res.data.message || 'No se pudo agregar.');
              return fetch((S.cartUrl || '/cart') + '.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
            })
            .then(function (cart) {
              setCartCount(cart.item_count);
              if (cartDrawer.open) cartDrawer.open(cart);
              else toast('Agregado al carrito', S.cartUrl || '/cart', 'Ver carrito');
            })
            .catch(function (err) {
              if (errEl) { errEl.textContent = err.message; errEl.hidden = false; }
            })
            .finally(function () { btn.classList.remove('is-loading'); });
        });
      }
    });
  }

  /* ==========================================================================
     Carrito lateral
     ========================================================================== */
  var cartDrawer = { open: null };
  function initCartDrawer() {
    var el = $('[data-cart-drawer]');
    if (!el || el._ready) return;
    el._ready = true;
    var body = $('[data-cart-drawer-items]', el);
    var foot = $('[data-cart-drawer-foot]', el);
    var total = $('[data-cart-drawer-total]', el);
    var count = $('[data-cart-drawer-count]', el);
    var lastFocus = null;
    var cartJs = (S.cartUrl || '/cart') + '.js';
    var changeJs = (S.cartChangeUrl || '/cart/change') + '.js';

    var esc = function (s) { var d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; };
    var thumb = function (u) { return u ? u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=240' : ''; };

    var render = function (cart, animate) {
      setCartCount(cart.item_count);
      count.textContent = cart.item_count ? '(' + cart.item_count + ')' : '';
      if (!cart.item_count) {
        body.innerHTML = '<div class="cart-drawer__empty"><p>Tu carrito está vacío.</p><a class="btn btn--dark" href="' + esc(S.allProductsUrl || '/collections/all') + '">Seguir comprando</a></div>';
        foot.hidden = true;
        return;
      }
      body.innerHTML = cart.items.map(function (it) {
        var variant = it.variant_title && it.variant_title !== 'Default Title' ? '<p class="cline__variant">' + esc(it.variant_title) + '</p>' : '';
        var img = it.image ? '<img src="' + esc(thumb(it.image)) + '" alt="' + esc(it.product_title) + '" loading="lazy">' : '';
        return '<div class="cline" data-key="' + esc(it.key) + '" data-qty="' + it.quantity + '">' +
          '<a class="cline__img" href="' + esc(it.url) + '">' + img + '</a>' +
          '<div class="cline__info"><a class="cline__title" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' + variant +
          '<div class="cline__qty"><button type="button" data-qty-change="-1" aria-label="Restar uno">−</button><span>' + it.quantity + '</span><button type="button" data-qty-change="1" aria-label="Sumar uno">+</button></div></div>' +
          '<div class="cline__side"><p class="cline__price">' + esc(formatMoney(it.final_line_price, S.moneyFormat)) + '</p><button type="button" class="cline__remove" data-remove>Quitar</button></div>' +
          '</div>';
      }).join('');
      total.textContent = formatMoney(cart.total_price, S.moneyFormat);
      foot.hidden = false;
      if (animate && MOTION) gsap.from($$('.cline', body), { autoAlpha: 0, x: 30, duration: 0.7, stagger: 0.06, delay: 0.15 });
    };

    var change = function (key, qty) {
      el.classList.add('is-busy');
      fetch(changeJs, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok || res.data.status) throw new Error(res.data.description || res.data.message || 'No se pudo actualizar.');
          render(res.data, false);
        })
        .catch(function (err) { toast(err.message); })
        .finally(function () { el.classList.remove('is-busy'); });
    };

    body.addEventListener('click', function (e) {
      var line = e.target.closest('.cline');
      if (!line) return;
      var step = e.target.closest('[data-qty-change]');
      if (step) change(line.dataset.key, Math.max(0, parseInt(line.dataset.qty, 10) + parseInt(step.dataset.qtyChange, 10)));
      if (e.target.closest('[data-remove]')) change(line.dataset.key, 0);
    });

    var open = function (cart) {
      lastFocus = document.activeElement;
      el.hidden = false;
      stopScroll();
      requestAnimationFrame(function () { el.classList.add('is-open'); });
      var closeBtn = $('.icon-btn[data-cart-close]', el);
      if (closeBtn) closeBtn.focus();
      var load = cart ? Promise.resolve(cart) : fetch(cartJs, { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
      load.then(function (c) { render(c, true); }).catch(function () {
        body.innerHTML = '<div class="cart-drawer__empty"><p>No pudimos cargar el carrito.</p><a class="btn btn--dark" href="' + esc(S.cartUrl || '/cart') + '">Ir al carrito</a></div>';
      });
    };
    var close = function () {
      el.classList.remove('is-open');
      startScroll();
      setTimeout(function () { el.hidden = true; if (lastFocus && lastFocus.focus) lastFocus.focus(); }, 500);
    };

    $$('[data-cart-close]', el).forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !el.hidden) close(); });
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-cart-open]');
      if (!trigger) return;
      e.preventDefault();
      open();
    });
    cartDrawer.open = open;
  }

  function initSort() {
    $$('[data-sort-select]').forEach(function (sel) {
      sel.addEventListener('change', function () { sel.form.submit(); });
    });
  }

  /* ==========================================================================
     Slider de campañas (funciona con y sin animaciones)
     ========================================================================== */
  function initSliders(scope) {
    $$('[data-slider]', scope).forEach(function (el) {
      if (el._slider) return;
      var slides = $$('[data-slide]', el);
      var bars = $$('[data-slide-to]', el);
      var track = $('[data-slider-track]', el);
      var dur = parseFloat(el.dataset.autoplay) || 6;
      var index = 0;
      var busy = false;
      var paused = REDUCE;
      var visible = true;
      var fill = null;
      var kenBurns = null;
      var parallax = null;
      var titles = new Map();
      var cleanups = [];

      if (paused) el.classList.add('is-paused');

      var imgOf = function (s) { return $('.slide__img, .placeholder', s); };
      var fillOf = function (i) { return bars[i] ? $('[data-bar-fill]', bars[i]) : null; };

      var setA11y = function (i) {
        slides.forEach(function (s, j) {
          var on = j === i;
          s.setAttribute('aria-hidden', on ? 'false' : 'true');
          $$('a, button', s).forEach(function (a) { if (on) a.removeAttribute('tabindex'); else a.setAttribute('tabindex', '-1'); });
        });
        bars.forEach(function (b, j) {
          b.classList.toggle('is-active', j === i);
          b.setAttribute('aria-selected', j === i ? 'true' : 'false');
        });
      };

      var resetFills = function () {
        bars.forEach(function (b, j) {
          var f = fillOf(j);
          if (!f) return;
          if (HAS_GSAP) gsap.set(f, { scaleX: 0 }); else f.style.transform = 'scaleX(0)';
        });
      };

      var stopFill = function () {
        if (!fill) return;
        if (fill.kill) fill.kill(); else clearTimeout(fill);
        fill = null;
      };

      var runFill = function () {
        stopFill();
        resetFills();
        if (paused || !visible || slides.length < 2) return;
        if (HAS_GSAP) {
          fill = gsap.fromTo(fillOf(index), { scaleX: 0 }, { scaleX: 1, duration: dur, ease: 'none', onComplete: function () { go(index + 1, 1); } });
        } else {
          fill = setTimeout(function () { go(index + 1, 1); }, dur * 1000);
        }
      };

      var startKenBurns = function (s) {
        if (!MOTION) return;
        if (kenBurns) kenBurns.kill();
        var img = imgOf(s);
        if (img) kenBurns = gsap.fromTo(img, { scale: 1.08 }, { scale: 1, duration: dur + 2, ease: 'none' });
      };

      var linesOf = function (s) {
        var t = $('[data-slide-title]', s);
        if (!t) return [];
        if (!canSplit()) return [t];
        if (!titles.has(t)) titles.set(t, SplitText.create(t, { type: 'lines', mask: 'lines', autoSplit: true }));
        return titles.get(t).lines;
      };

      var revealText = function (tl, s, at) {
        tl.fromTo(linesOf(s), { yPercent: 110 }, { yPercent: 0, duration: 1.1, stagger: 0.09 }, at);
        tl.fromTo($$('[data-slide-fade]', s), { autoAlpha: 0, y: 26 }, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.08 }, at + 0.15);
      };

      var go = function (n, dir) {
        if (busy || slides.length < 2) return;
        n = (n + slides.length) % slides.length;
        if (n === index) return;
        dir = dir || (n > index ? 1 : -1);
        var from = slides[index];
        var to = slides[n];
        index = n;
        setA11y(n);
        el.classList.toggle('is-dark-ui', to.classList.contains('slide--text-dark'));
        stopFill();
        resetFills();
        if (!MOTION) {
          from.classList.remove('is-active');
          to.classList.add('is-active');
          runFill();
          return;
        }
        busy = true;
        to.classList.add('is-entering');
        var fromImg = imgOf(from);
        var toImg = imgOf(to);
        if (kenBurns) kenBurns.kill();
        var tl = gsap.timeline({
          onComplete: function () {
            from.classList.remove('is-active');
            to.classList.remove('is-entering');
            to.classList.add('is-active');
            gsap.set(to, { clearProps: 'clipPath' });
            if (fromImg) gsap.set(fromImg, { xPercent: 0 });
            busy = false;
            startKenBurns(to);
            runFill();
          }
        });
        var curtain = window.CustomEase ? 'curtain' : 'expo.inOut';
        tl.fromTo(to, { clipPath: dir > 0 ? 'inset(0% 0% 0% 100%)' : 'inset(0% 100% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.15, ease: curtain }, 0);
        if (toImg) tl.fromTo(toImg, { scale: 1.3, xPercent: dir * 10 }, { scale: 1.08, xPercent: 0, duration: 1.5 }, 0);
        if (fromImg) tl.to(fromImg, { xPercent: -dir * 14, duration: 1.15, ease: curtain }, 0);
        revealText(tl, to, 0.45);
      };

      var play = function () {
        paused = false;
        el.classList.remove('is-paused');
        var toggle = $('[data-slider-toggle]', el);
        if (toggle) toggle.setAttribute('aria-label', 'Pausar');
        if (fill && fill.paused && fill.paused()) fill.resume(); else runFill();
      };
      var pause = function () {
        paused = true;
        el.classList.add('is-paused');
        var toggle = $('[data-slider-toggle]', el);
        if (toggle) toggle.setAttribute('aria-label', 'Reproducir');
        if (fill && fill.pause) fill.pause(); else stopFill();
      };

      // Controles
      var on = function (node, type, fn, opts) {
        if (!node) return;
        node.addEventListener(type, fn, opts);
        cleanups.push(function () { node.removeEventListener(type, fn, opts); });
      };
      on($('[data-slider-next]', el), 'click', function () { go(index + 1, 1); });
      on($('[data-slider-prev]', el), 'click', function () { go(index - 1, -1); });
      on($('[data-slider-toggle]', el), 'click', function () { paused ? play() : pause(); });
      bars.forEach(function (b) {
        on(b, 'click', function () { var n = parseInt(b.dataset.slideTo, 10); go(n, n > index ? 1 : -1); });
      });
      on(el, 'keydown', function (e) {
        if (e.key === 'ArrowRight') go(index + 1, 1);
        if (e.key === 'ArrowLeft') go(index - 1, -1);
      });

      // Deslizar con el dedo
      var x0 = null;
      on(el, 'pointerdown', function (e) { if (!e.target.closest('a, button')) x0 = e.clientX; });
      on(el, 'pointerup', function (e) {
        if (x0 === null) return;
        var dx = e.clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 50) go(index + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
      });

      // Pausa automática fuera de pantalla o con la pestaña oculta
      var setVisible = function (v) {
        visible = v;
        if (!fill) { if (v && !paused) runFill(); return; }
        if (!v && fill.pause) fill.pause();
        else if (v && !paused && fill.resume) fill.resume();
      };
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) { setVisible(entries[0].isIntersecting); }, { threshold: 0.2 });
        io.observe(el);
        cleanups.push(function () { io.disconnect(); });
      }
      on(document, 'visibilitychange', function () { setVisible(!document.hidden); });

      // Parallax suave del slider al bajar
      if (MOTION && track) {
        parallax = gsap.to(track, { yPercent: 18, ease: 'none', scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: true } });
      }

      setA11y(0);
      el.classList.toggle('is-dark-ui', slides[0].classList.contains('slide--text-dark'));
      resetFills();
      // Entrada de la primera campaña (después de la cortina)
      whenReady(function () {
        if (!el._slider) return;
        if (MOTION) {
          var tl = gsap.timeline();
          var img = imgOf(slides[0]);
          if (img) tl.fromTo(img, { scale: 1.25 }, { scale: 1.08, duration: 1.6 }, 0);
          revealText(tl, slides[0], 0.1);
          tl.add(function () { startKenBurns(slides[0]); }, 1.2);
        }
        runFill();
      });

      el._slider = {
        go: go, pause: pause, play: play,
        indexOf: function (s) { return slides.indexOf(s); },
        destroy: function () {
          stopFill();
          if (kenBurns) kenBurns.kill();
          if (parallax) { parallax.scrollTrigger && parallax.scrollTrigger.kill(); parallax.kill(); }
          titles.forEach(function (sp) { sp.revert(); });
          cleanups.forEach(function (fn) { fn(); });
          el._slider = null;
        }
      };
    });
  }

  /* Barra de anuncios que rota */
  function initAnnounce() {
    var bar = $('[data-announce]');
    if (!bar || bar._rot) return;
    var items = $$('[data-announce-item]', bar);
    if (items.length < 2) return;
    var i = 0;
    bar._rot = setInterval(function () {
      if (document.hidden) return;
      var cur = items[i];
      i = (i + 1) % items.length;
      cur.classList.remove('is-active');
      cur.classList.add('is-leaving');
      items[i].classList.add('is-active');
      setTimeout(function () { cur.classList.remove('is-leaving'); }, 700);
    }, 4000);
  }

  /* ==========================================================================
     Arranque
     ========================================================================== */
  initHeader();
  initAnnounce();
  initMenu();
  initProduct(document);
  initSort();
  initCartDrawer();
  initSliders(document);

  if (MOTION) {
    fontsReady().then(function () {
      mount(document);
      runIntro();
      ScrollTrigger.refresh();
    });
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  } else {
    var intro = $('[data-intro]');
    if (intro) intro.parentNode.removeChild(intro);
    signalReady();
  }

  /* Editor de temas de Shopify */
  document.addEventListener('shopify:section:load', function (e) {
    initProduct(e.target);
    initSliders(e.target);
    initAnnounce();
    if (!MOTION) return;
    mount(e.target);
    ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    $$('[data-slider]', e.target).forEach(function (s) { if (s._slider) s._slider.destroy(); });
    var bar = $('[data-announce]', e.target);
    if (bar && bar._rot) clearInterval(bar._rot);
    if (MOTION) unmount(e.target);
  });
  // Al seleccionar una campaña en el editor, mostrarla y pausar
  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target.closest && e.target.closest('[data-slide]');
    var el = slide && slide.closest('[data-slider]');
    if (!el || !el._slider) return;
    el._slider.pause();
    el._slider.go(el._slider.indexOf(slide));
  });

  window.Atelier = { lenis: lenis, formatMoney: formatMoney, toast: toast, openCart: function () { if (cartDrawer.open) cartDrawer.open(); } };
})();
